import { useState } from "react";
import { Plus } from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { dateToX, daysBetween, parseDate, xToDate } from "@/lib/gantt-utils";
import DateMarker from "./DateMarker";
import GanttEventIcon from "./GanttEventIcon";

// Single-task row inside the gantt grid. Responsibilities:
//   - compute bars (paired date columns) and markers (single date columns)
//   - milestone mode → one diamond instead of bars
//   - hover guide + click anywhere → open event popover at that day
//   - render attached events on top
export default function GanttRow({
  task,
  dateCols,
  range,
  pxPerDay,
  width,
  canEdit,
  isCritical,
  isMilestone,
  onOpenEventPopover,
  onDeleteEvent,
}) {
  const [hoverX, setHoverX] = useState(null);
  const dates = task.dates || {};

  const bars = [];
  const markers = [];
  dateCols.forEach((col) => {
    const value = dates[col.id];
    if (!value) return;
    const x = dateToX(parseDate(value), range.start, pxPerDay);
    if (x == null) return;
    if (col.pair_with && dates[col.pair_with]) {
      const x2 = dateToX(parseDate(dates[col.pair_with]), range.start, pxPerDay);
      if (x2 != null && x2 > x) {
        bars.push({
          col,
          x,
          width: Math.max(6, x2 - x),
          label: col.label,
          days: daysBetween(value, dates[col.pair_with]),
        });
        return;
      }
    }
    markers.push({ col, x, value });
  });

  const showMilestone = isMilestone && markers.length + bars.length > 0;
  const milestoneX = (() => {
    if (!showMilestone) return null;
    if (bars.length > 0) return bars[0].x;
    return markers[0].x;
  })();

  const handleRowClick = (e) => {
    if (!canEdit) return;
    if (e.target.closest("[data-event-anchor]")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const date = xToDate(localX, range.start, pxPerDay);
    onOpenEventPopover({
      taskId: task.id,
      anchorRect: { top: e.clientY, left: e.clientX, right: e.clientX, bottom: e.clientY },
      event_date: date,
    });
  };

  const handleRowMove = (e) => {
    if (!canEdit) {
      if (hoverX != null) setHoverX(null);
      return;
    }
    if (e.target.closest("[data-event-anchor]")) {
      if (hoverX != null) setHoverX(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverX(e.clientX - rect.left);
  };

  return (
    <div
      className={`relative h-10 border-b border-slate-100 hover:bg-slate-100/40 group transition-colors ${isCritical ? "bg-red-50/50 ring-1 ring-inset ring-red-300" : ""} ${canEdit ? "cursor-cell" : ""}`}
      style={{ width }}
      data-testid={`gantt-row-${task.id}`}
      onClick={handleRowClick}
      onMouseMove={handleRowMove}
      onMouseLeave={() => setHoverX(null)}
    >
      {canEdit && hoverX != null && (
        <div
          className="absolute top-0 bottom-0 w-px bg-slate-950/30 pointer-events-none z-[5]"
          style={{ left: Math.floor(hoverX / pxPerDay) * pxPerDay + pxPerDay / 2 }}
          aria-hidden="true"
        >
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 h-5 w-5 rounded-full bg-slate-950 text-white flex items-center justify-center shadow-md">
            <Plus size={10} weight="bold" />
          </div>
        </div>
      )}

      {showMilestone && milestoneX != null && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                data-event-anchor="milestone"
                className={`absolute z-[12] cursor-pointer ${isCritical ? "critical-bar" : ""}`}
                style={{ left: milestoneX, top: "50%", transform: "translate(-50%, -50%)" }}
                onClick={(e) => {
                  if (!canEdit) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  onOpenEventPopover({
                    taskId: task.id,
                    anchorRect: rect,
                    field_ref: markers[0]?.col.id || bars[0]?.col.id,
                  });
                }}
                data-testid={`gantt-milestone-${task.id}`}
              >
                <div
                  className="w-4 h-4 rotate-45 border-2 border-white shadow-md"
                  style={{ background: isCritical ? "#dc2626" : "#0f172a" }}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent className="rounded-md text-xs">
              <div className="font-semibold">{task.task || task.subtask}</div>
              <div className="text-slate-300">Milestone</div>
              {isCritical && <div className="text-red-300 font-semibold mt-1">ON CRITICAL PATH</div>}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {!showMilestone &&
        bars.map((b, i) => (
          <TooltipProvider key={`bar-${i}`} delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  data-event-anchor="bar"
                  onClick={(e) => {
                    if (!canEdit) return;
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    onOpenEventPopover({ taskId: task.id, anchorRect: rect, field_ref: b.col.id });
                  }}
                  className={`absolute h-5 rounded-md flex items-center px-2 text-[10px] text-white font-medium shadow-sm z-10 cursor-pointer transition-all hover:h-6 hover:shadow ${isCritical ? "ring-2 ring-red-500 ring-offset-1 critical-bar" : ""}`}
                  style={{
                    top: "50%",
                    transform: "translateY(-50%)",
                    left: b.x,
                    width: b.width,
                    background: b.col.color,
                  }}
                  data-testid={`gantt-bar-${task.id}-${b.col.id}`}
                >
                  <span className="truncate">
                    {b.days}d · {task.task || task.subtask}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="rounded-md text-xs">
                <div className="font-semibold">{task.task || task.subtask}</div>
                <div className="text-slate-400">
                  {b.col.label} → {b.col.pair_with ? dateCols.find((c) => c.id === b.col.pair_with)?.label : ""}
                </div>
                <div className="font-mono-ibm">{b.days} days</div>
                {isCritical && <div className="text-red-300 font-semibold mt-1">ON CRITICAL PATH</div>}
                {canEdit && <div className="text-[10px] text-slate-400 mt-1 italic">Click bar to add comment/icon</div>}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}

      {!showMilestone &&
        markers.map((m, i) => (
          <DateMarker
            key={`m-${i}`}
            col={m.col}
            x={m.x}
            value={m.value}
            taskName={task.task || task.subtask}
            canEdit={canEdit}
            isCritical={isCritical}
            onClick={(anchorRect) =>
              onOpenEventPopover({ taskId: task.id, anchorRect, field_ref: m.col.id })
            }
          />
        ))}

      {(task.events || []).map((ev) => {
        // Anchor priority: event_date → field_ref → first known date column
        let ax = null;
        if (ev.event_date) {
          ax = dateToX(parseDate(ev.event_date), range.start, pxPerDay);
        }
        if (ax == null && ev.field_ref && dates[ev.field_ref]) {
          ax = dateToX(parseDate(dates[ev.field_ref]), range.start, pxPerDay);
        }
        if (ax == null) {
          for (const col of dateCols) {
            if (dates[col.id]) {
              ax = dateToX(parseDate(dates[col.id]), range.start, pxPerDay);
              break;
            }
          }
        }
        if (ax == null) ax = 4;
        return (
          <GanttEventIcon
            key={ev.id}
            event={ev}
            x={ax}
            canEdit={canEdit}
            onDelete={() => onDeleteEvent(task.id, ev.id)}
          />
        );
      })}

      {canEdit && (
        <button
          type="button"
          data-event-anchor="add"
          onClick={(e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            onOpenEventPopover({ taskId: task.id, anchorRect: rect });
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 h-6 w-6 rounded-md bg-white border border-slate-300 text-slate-600 hover:text-slate-950 hover:border-slate-400 flex items-center justify-center z-30 transition-opacity"
          data-testid={`add-event-${task.id}`}
          title="Add icon / comment"
        >
          <Plus size={12} weight="bold" />
        </button>
      )}
    </div>
  );
}
